import { HttpApiService } from "@/shared/services/httpApi.service";

import { INTERNAL_API_KEYS_URL } from "../constants/internalApiKeys.constants";
import type {
  InternalApiKey,
  InternalApiKeyAccess,
  InternalApiKeyPayload,
  InternalApiKeyUsagePage,
  InternalApiKeyWithToken,
} from "../types/internalApiKeys.types";

const withCode = (code: string) => ({ headers: { "x-two-factor-code": code } });

/** `/internal/internal-api-keys` — the only place that knows the wire. */
export const InternalApiKeysService = {
  async access(): Promise<InternalApiKeyAccess> {
    const { data } = await HttpApiService.get<InternalApiKeyAccess>(`${INTERNAL_API_KEYS_URL}/access`);
    return data;
  },

  async list(): Promise<InternalApiKey[]> {
    const { data } = await HttpApiService.get<InternalApiKey[]>(INTERNAL_API_KEYS_URL);
    return data;
  },

  async usage(id: string, params: { skip: number; limit: number }): Promise<InternalApiKeyUsagePage> {
    const { data } = await HttpApiService.get<InternalApiKeyUsagePage>(
      `${INTERNAL_API_KEYS_URL}/${id}/usage`,
      {
        params,
      },
    );
    return data;
  },

  async create(payload: InternalApiKeyPayload, code: string): Promise<InternalApiKeyWithToken> {
    const { data } = await HttpApiService.post<InternalApiKeyWithToken>(
      INTERNAL_API_KEYS_URL,
      payload,
      withCode(code),
    );
    return data;
  },

  async update(id: string, payload: Partial<InternalApiKeyPayload>, code: string): Promise<InternalApiKey> {
    const { data } = await HttpApiService.patch<InternalApiKey>(
      `${INTERNAL_API_KEYS_URL}/${id}`,
      payload,
      withCode(code),
    );
    return data;
  },

  async enable(id: string, code: string): Promise<InternalApiKey> {
    const { data } = await HttpApiService.post<InternalApiKey>(
      `${INTERNAL_API_KEYS_URL}/${id}/enable`,
      {},
      withCode(code),
    );
    return data;
  },

  async disable(id: string): Promise<InternalApiKey> {
    const { data } = await HttpApiService.post<InternalApiKey>(`${INTERNAL_API_KEYS_URL}/${id}/disable`, {});
    return data;
  },

  /**
   * `retirePreviousNow` is for a leaked token: the old secret stops at once instead of after the grace
   * window.
   */
  async rotate(id: string, code: string, retirePreviousNow = false): Promise<InternalApiKeyWithToken> {
    const { data } = await HttpApiService.post<InternalApiKeyWithToken>(
      `${INTERNAL_API_KEYS_URL}/${id}/rotate`,
      retirePreviousNow ? { retire_previous_now: true } : {},
      withCode(code),
    );
    return data;
  },

  async revoke(id: string): Promise<InternalApiKey> {
    const { data } = await HttpApiService.delete<InternalApiKey>(`${INTERNAL_API_KEYS_URL}/${id}`);
    return data;
  },
};
